import React, { useEffect, useMemo, useState } from 'react';
import { authToken } from '../authToken';
import { API_BASE_URL } from '../config';

function formatTimestamp(ts) {
  const x = Number(ts);
  if (!Number.isFinite(x) || x <= 0) return '';
  try {
    return new Date(x).toLocaleString();
  } catch {
    return String(ts);
  }
}

function getSignedAmountStyle(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return undefined;
  return {
    color: n > 0 ? '#c00' : '#0a0',
    fontWeight: 800,
  };
}

function formatDateInputUTC(tsMillis) {
  const t = Number(tsMillis);
  const d = Number.isFinite(t) ? new Date(t) : new Date();
  // Use UTC so the date string is stable and "timezone-less" like tasks/events.
  return d.toISOString().slice(0, 10);
}

function toUtcMidnightMillis(dateStr) {
  const s = String(dateStr || '').trim();
  if (!s) return NaN;
  // Match tasks/events logic: always store 00:00:00 UTC
  const d = new Date(`${s}T00:00:00.000Z`);
  const t = d.getTime();
  return Number.isFinite(t) ? t : NaN;
}

export default function CashflowTrackerPanel({ onClose }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showAddPanel, setShowAddPanel] = useState(false);
  const [menuEntryId, setMenuEntryId] = useState(null);
  const [confirmDeleteEntryId, setConfirmDeleteEntryId] = useState(null);

  // Add form state
  const [newName, setNewName] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [newAmount, setNewAmount] = useState('');
  // Use date-only selection like tasks/events; store as UTC midnight epoch millis.
  const [newDateStr, setNewDateStr] = useState(() => formatDateInputUTC(Date.now()));
  const [addError, setAddError] = useState(null);

  const fetchEntries = () => {
    setLoading(true);
    setError(null);

    fetch(`${API_BASE_URL}/cashflowtrackerentries`, {
      headers: {
        ...(authToken ? { Authorization: authToken } : {}),
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch cashflow tracker entries');
        return res.json();
      })
      .then((data) => {
        setEntries(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err?.message || 'Failed to fetch cashflow tracker entries');
        setEntries([]);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sortedEntries = useMemo(() => {
    return [...(entries || [])].sort((a, b) => {
      const ta = Number(a?.timestamp) || 0;
      const tb = Number(b?.timestamp) || 0;
      if (ta !== tb) return tb - ta; // newest first
      const na = String(a?.name ?? '').toLowerCase();
      const nb = String(b?.name ?? '').toLowerCase();
      return na.localeCompare(nb);
    });
  }, [entries]);

  const summary = useMemo(() => {
    const total = (entries || []).reduce((sum, e) => sum + (Number(e?.amount) || 0), 0);
    const byCategory = (entries || []).reduce((acc, e) => {
      const cat = String(e?.category ?? '').trim() || '(no category)';
      acc[cat] = (acc[cat] || 0) + (Number(e?.amount) || 0);
      return acc;
    }, {});

    const byCategorySorted = Object.entries(byCategory)
      .sort(([, a], [, b]) => (Number(b) || 0) - (Number(a) || 0));

    return { total, byCategorySorted };
  }, [entries]);

  const handleAdd = () => {
    setAddError(null);

    const name = String(newName || '').trim();
    const category = String(newCategory || '').trim();
    const amountNum = Number(newAmount);

    const tsNum = toUtcMidnightMillis(newDateStr);
    if (!Number.isFinite(tsNum) || !Number.isInteger(tsNum)) {
      setAddError('Date is invalid.');
      return;
    }

    if (!name) {
      setAddError('Name is required.');
      return;
    }
    if (!category) {
      setAddError('Category is required.');
      return;
    }
    if (!Number.isFinite(amountNum) || !Number.isInteger(amountNum)) {
      setAddError('Amount must be an integer.');
      return;
    }

    fetch(`${API_BASE_URL}/cashflowtrackerentries`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { Authorization: authToken } : {}),
      },
      body: JSON.stringify({
        name,
        category,
        amount: amountNum,
        timestamp: tsNum,
      }),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to add entry');
        return res.json().catch(() => null);
      })
      .then(() => {
        setShowAddPanel(false);
        setNewName('');
        setNewCategory('');
        setNewAmount('');
        setNewDateStr(formatDateInputUTC(Date.now()));
        fetchEntries();
      })
      .catch((err) => setAddError(err?.message || 'Failed to add entry'));
  };

  const deleteEntry = (id) => {
    if (id == null) return;

    fetch(`${API_BASE_URL}/cashflowtrackerentries/${id}`, {
      method: 'DELETE',
      headers: {
        ...(authToken ? { Authorization: authToken } : {}),
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to delete entry');
      })
      .then(() => {
        setConfirmDeleteEntryId(null);
        setMenuEntryId(null);
        fetchEntries();
      })
      .catch((err) => setError(err?.message || 'Failed to delete entry'));
  };

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 16,
        padding: '24px 12px 18px 12px',
        boxShadow: '0 2px 18px rgba(0,0,0,0.22)',
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        minWidth: 0,
        width: '780px',
        maxWidth: '98vw',
        minHeight: 0,
        height: '700px',
        maxHeight: '96vh',
        overflow: 'auto',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <button
        onClick={onClose}
        style={{ position: 'absolute', top: 16, right: 16, fontSize: 24, background: 'none', border: 'none', cursor: 'pointer' }}
        aria-label="Close"
      >
        ✖️
      </button>

      <h2 style={{ marginBottom: 16 }}>Cashflow tracker</h2>

      <div
        style={{
          display: 'flex',
          gap: 24,
          marginBottom: 18,
          fontSize: '1.08rem',
          fontWeight: 500,
          color: '#333',
          justifyContent: 'space-between',
          background: '#eef8ff',
          borderRadius: 8,
          padding: '10px 18px',
          border: '1px solid #09f',
          flexWrap: 'wrap',
        }}
      >
        <span>
          Wpisów: <b>{entries.length}</b>
        </span>
        <span>
          Suma: <b style={getSignedAmountStyle(summary.total)}>{summary.total}</b>
        </span>
      </div>

      {summary.byCategorySorted.length > 0 && (
        <div style={{
          marginBottom: 18,
          borderRadius: 8,
          background: '#f8f8f8',
          border: '1px solid #eee',
          padding: '10px 12px',
          fontSize: '0.95rem'
        }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Kategorie (suma)</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {summary.byCategorySorted.slice(0, 12).map(([cat, sum]) => (
              <div
                key={cat}
                style={{
                  background: '#fff',
                  border: '1px solid #eee',
                  borderRadius: 999,
                  padding: '6px 10px',
                }}
              >
                <span style={{ fontWeight: 600 }}>{cat}</span>{' '}
                <span style={getSignedAmountStyle(sum)}>{Number(sum) || 0}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, marginBottom: 18 }}>
        <button
          style={{
            flex: 1,
            padding: '10px 0',
            fontSize: '1.1rem',
            fontWeight: 600,
            borderRadius: 8,
            border: '1px solid #09f',
            background: '#eef8ff',
            color: '#036',
            cursor: 'pointer',
            transition: 'background 0.2s',
          }}
          onClick={() => setShowAddPanel(true)}
        >
          ➕ Dodaj
        </button>
      </div>

      {loading && <div>Loading...</div>}
      {error && <div style={{ color: 'red' }}>Error: {error}</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {sortedEntries.map((e) => (
          <div
            key={e.id ?? `${e.name}-${e.category}-${e.amount}-${e.timestamp}`}
            style={{
              border: '1px solid #eee',
              borderRadius: 10,
              padding: '10px 12px',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 12,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                <div style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {e?.name}
                </div>
                <div style={{ whiteSpace: 'nowrap' }}>
                  <span style={getSignedAmountStyle(e?.amount)}>{e?.amount}</span>
                </div>
              </div>
              <div style={{ marginTop: 4, fontSize: '0.86rem', color: '#666' }}>
                <span>{e?.category}</span>
                {e?.timestamp != null && (
                  <>
                    <span style={{ margin: '0 8px', color: '#bbb' }}>•</span>
                    <span>{formatTimestamp(e?.timestamp)}</span>
                  </>
                )}
              </div>
            </div>

            <div style={{ position: 'relative' }}>
              <button
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 22,
                  cursor: 'pointer',
                  color: '#888',
                  marginLeft: 8,
                }}
                aria-label="Menu"
                onClick={(evt) => {
                  evt.stopPropagation();
                  setMenuEntryId(e.id);
                }}
              >
                ⋯
              </button>

              {menuEntryId === e.id && (
                <div
                  style={{
                    position: 'absolute',
                    background: '#fff',
                    border: '1px solid #eee',
                    borderRadius: 8,
                    boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
                    padding: 12,
                    zIndex: 12000,
                    right: 24,
                    top: 32,
                  }}
                >
                  <button
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '8px 0',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#a22',
                    }}
                    onClick={() => {
                      setMenuEntryId(null);
                      setConfirmDeleteEntryId(e.id);
                    }}
                  >
                    Delete
                  </button>
                  <button
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '8px 0',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#888',
                    }}
                    onClick={() => setMenuEntryId(null)}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>

            {confirmDeleteEntryId === e.id && (
              <div
                style={{
                  position: 'fixed',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%,-50%)',
                  background: '#fff',
                  borderRadius: 12,
                  boxShadow: '0 2px 16px rgba(0,0,0,0.18)',
                  padding: 28,
                  zIndex: 13000,
                  minWidth: 260,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 16,
                }}
              >
                <div style={{ fontSize: '1.1rem', marginBottom: 8 }}>
                  Delete entry <b>{e?.name}</b>?
                </div>
                <div style={{ display: 'flex', gap: 16 }}>
                  <button
                    onClick={() => deleteEntry(e.id)}
                    style={{
                      padding: '10px 18px',
                      background: '#c00',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontWeight: 700,
                    }}
                  >
                    Delete
                  </button>
                  <button
                    onClick={() => setConfirmDeleteEntryId(null)}
                    style={{
                      padding: '10px 18px',
                      background: '#eee',
                      color: '#444',
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontWeight: 700,
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}

        {!loading && sortedEntries.length === 0 && (
          <div style={{ color: '#666', padding: 8 }}>No entries yet.</div>
        )}
      </div>

      {showAddPanel && (
        <div
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%,-50%)',
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 2px 16px rgba(0,0,0,0.18)',
            padding: 28,
            zIndex: 14000,
            width: '560px',
            maxWidth: '96vw',
          }}
        >
          <div style={{ fontSize: '1.2rem', fontWeight: 800, marginBottom: 16 }}>Add entry</div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontWeight: 600 }}>Name</span>
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                style={{ padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
              />
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontWeight: 600 }}>Category</span>
              <input
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                style={{ padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
              />
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontWeight: 600 }}>Amount (integer)</span>
              <input
                value={newAmount}
                onChange={(e) => setNewAmount(e.target.value)}
                style={{ padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
                inputMode="numeric"
              />
            </label>

            <div>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Date</div>
              <input
                type="date"
                value={newDateStr}
                onChange={(e) => setNewDateStr(e.target.value)}
                style={{ padding: 10, borderRadius: 8, border: '1px solid #ddd', width: '100%', fontSize: '1.0rem' }}
              />
              <div style={{ marginTop: 8, fontSize: '0.86rem', color: '#666' }}>
                UTC midnight millis: <span style={{ fontFamily: 'monospace' }}>{toUtcMidnightMillis(newDateStr)}</span>
              </div>
            </div>

            {addError && <div style={{ color: 'red', marginTop: 6 }}>{addError}</div>}

            <div style={{ display: 'flex', gap: 12, marginTop: 10 }}>
              <button
                onClick={handleAdd}
                style={{
                  flex: 1,
                  padding: '10px 18px',
                  background: '#09f',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontWeight: 800,
                }}
              >
                Add
              </button>
              <button
                onClick={() => {
                  setShowAddPanel(false);
                  setNewDateStr(formatDateInputUTC(Date.now()));
                }}
                style={{
                  flex: 1,
                  padding: '10px 18px',
                  background: '#eee',
                  color: '#444',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontWeight: 800,
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
